import { ITEM, SCORE, TILE } from '../config/config';
import type { World } from '../game/world';
import { C } from '../gfx/palette';
import type { Renderer } from '../gfx/renderer';
import { moveBody } from '../world/physics';
import { Entity } from './entity';
import type { Player } from './player';

/** Shared behaviour for anything that rises out of a block and can be picked up. */
export abstract class Pickup extends Entity {
  readonly kind = 'item' as const;
  /** Remaining emerge time; while > 0 the item slides up out of its block. */
  protected emerge = 0;
  private emergeFrom = 0;
  private emergeTo = 0;

  constructor(x: number, y: number, w: number, h: number) {
    super(x, y, w, h);
    this.alwaysAwake = true;
    this.awake = true;
    this.z = 1;
  }

  /** Start inside the block at `blockY` and slide up one tile. */
  startEmerge(blockY: number): void {
    this.emergeFrom = blockY + TILE - this.h;
    this.emergeTo = blockY - this.h;
    this.y = this.emergeFrom;
    this.emerge = ITEM.emergeTime;
  }

  protected updateEmerge(dt: number): boolean {
    if (this.emerge <= 0) return false;
    this.emerge -= dt;
    const t = 1 - Math.max(0, this.emerge) / ITEM.emergeTime;
    this.y = this.emergeFrom + (this.emergeTo - this.emergeFrom) * Math.min(1, t);
    if (this.emerge <= 0) {
      this.y = this.emergeTo;
      this.onEmerged();
    }
    return true;
  }

  protected onEmerged(): void {
    /* subclasses may start moving here */
  }

  /** Called when the player overlaps this pickup. */
  abstract collect(world: World, player: Player): void;
}

/** Sunberry: grows the player. Walks along the ground and turns at walls. */
export class Sunberry extends Pickup {
  constructor(x: number, y: number) {
    super(x, y, 14, 14);
    this.facing = 1;
  }

  protected override onEmerged(): void {
    this.vx = ITEM.berrySpeed * this.facing;
  }

  override update(dt: number, world: World): void {
    if (this.updateEmerge(dt)) return;
    this.vy = Math.min(this.vy + 900 * dt, 260);
    moveBody(this, world.map, dt, world.solidBoxes());
    if (this.hitLeft) {
      this.facing = 1;
      this.vx = ITEM.berrySpeed;
    } else if (this.hitRight) {
      this.facing = -1;
      this.vx = -ITEM.berrySpeed;
    }
    if (this.y > world.map.pixelH + 32) this.alive = false;
  }

  override collect(world: World, player: Player): void {
    this.alive = false;
    player.grow(world);
    world.addScore(SCORE.berry, this.centerX, this.y);
  }

  override draw(r: Renderer): void {
    r.sprite('berry', this.x, this.y);
  }
}

/** Ember Seed: grants the orb attack. Stays where it appears. */
export class EmberSeed extends Pickup {
  private anim = 0;

  constructor(x: number, y: number) {
    super(x, y, 14, 14);
  }

  override update(dt: number, world: World): void {
    this.anim += dt;
    if (this.updateEmerge(dt)) return;
    // settle onto the ground if the block was destroyed underneath
    this.vy = Math.min(this.vy + 700 * dt, 240);
    moveBody(this, world.map, dt, world.solidBoxes());
    if (this.y > world.map.pixelH + 32) this.alive = false;
  }

  override collect(world: World, player: Player): void {
    this.alive = false;
    player.giveFire(world);
    world.addScore(SCORE.ember, this.centerX, this.y);
  }

  override draw(r: Renderer): void {
    r.sprite(`ember.${Math.floor(this.anim * 8) % 2}`, this.x, this.y);
  }
}

/** Prism Shard: temporary invincibility. Bounces as it travels. */
export class PrismShard extends Pickup {
  private anim = 0;

  constructor(x: number, y: number) {
    super(x, y, 14, 14);
    this.facing = 1;
  }

  protected override onEmerged(): void {
    this.vx = ITEM.prismSpeed;
    this.vy = -ITEM.prismBounce * 0.6;
  }

  override update(dt: number, world: World): void {
    this.anim += dt;
    if (this.updateEmerge(dt)) return;
    this.vy = Math.min(this.vy + 1000 * dt, 300);
    moveBody(this, world.map, dt, world.solidBoxes());
    if (this.onGround) this.vy = -ITEM.prismBounce;
    if (this.hitLeft) this.vx = ITEM.prismSpeed;
    else if (this.hitRight) this.vx = -ITEM.prismSpeed;
    if (this.y > world.map.pixelH + 32) this.alive = false;
    if (world.rng.bool(0.25)) {
      world.particles.emit(
        this.centerX + world.rng.range(-6, 6),
        this.centerY + world.rng.range(-6, 6),
        0,
        -20,
        0.3,
        C.prismLight,
        1,
        0,
        1,
      );
    }
  }

  override collect(world: World, player: Player): void {
    this.alive = false;
    player.givePrism(world);
    world.addScore(SCORE.prism, this.centerX, this.y);
    world.sfx('sprout');
  }

  override draw(r: Renderer): void {
    r.sprite(`prism.${Math.floor(this.anim * 12) % 2}`, this.x, this.y);
  }
}

/** Heart Pod: one extra life. */
export class HeartPod extends Pickup {
  constructor(x: number, y: number) {
    super(x, y, 14, 14);
    this.facing = 1;
  }

  protected override onEmerged(): void {
    this.vx = ITEM.berrySpeed * this.facing;
  }

  override update(dt: number, world: World): void {
    if (this.updateEmerge(dt)) return;
    this.vy = Math.min(this.vy + 900 * dt, 260);
    moveBody(this, world.map, dt, world.solidBoxes());
    if (this.hitLeft) this.vx = ITEM.berrySpeed;
    else if (this.hitRight) this.vx = -ITEM.berrySpeed;
    if (this.y > world.map.pixelH + 32) this.alive = false;
  }

  override collect(world: World, _player: Player): void {
    this.alive = false;
    world.addLife(this.centerX, this.y);
  }

  override draw(r: Renderer): void {
    r.sprite('pod', this.x, this.y);
  }
}

/** Sunbit lying in the world (placed by the level, not by a block). */
export class Sunbit extends Entity {
  readonly kind = 'coin' as const;
  private anim = 0;

  constructor(x: number, y: number) {
    super(x + 3, y + 1, 10, 14);
    this.z = 0;
  }

  override update(dt: number, world: World): void {
    this.anim += dt;
    void world;
  }

  collect(world: World): void {
    this.alive = false;
    world.addCoin(1);
    world.addScore(SCORE.coin, this.centerX, this.y);
    world.sfx('coin');
    world.particles.coinSparkle(this.centerX, this.centerY);
  }

  override draw(r: Renderer): void {
    r.sprite(`coin.${Math.floor(this.anim * 9) % 4}`, this.x, this.y);
  }
}

/** The coin that pops out of a block: purely cosmetic, awards on spawn. */
export class PopCoin extends Entity {
  readonly kind = 'fx' as const;
  private anim = 0;
  private life = 0.75;

  constructor(x: number, y: number) {
    super(x + 3, y, 10, 14);
    this.vy = -ITEM.coinPopVy;
    this.alwaysAwake = true;
    this.awake = true;
    this.z = 3;
  }

  override update(dt: number, world: World): void {
    this.anim += dt;
    this.life -= dt;
    this.vy += 900 * dt;
    this.y += this.vy * dt;
    if (this.life <= 0) {
      this.alive = false;
      world.particles.coinSparkle(this.centerX, this.centerY);
    }
  }

  override draw(r: Renderer): void {
    r.sprite(`coin.${Math.floor(this.anim * 24) % 4}`, this.x, this.y);
  }
}
